import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConsoleCard } from "@/components/ui/console";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import { SVC_CONFIG_TREE, SVC_CONFIG_META } from "@/data/mock";

/* SVC-03 — Effective Config Viewer.
   Canonical source: prototype/pages/admin/config-viewer.html (no current
   Figma frame located). Read-only hierarchical config tree with
   inherited/override/default source tags; every key/value/source below
   is transcribed verbatim from the source. "Download JSON"/"Download
   YAML" reproduce the canonical source's own honest toast text verbatim
   ("... downloaded (production only)") — this deferral is the source's
   own, not invented here. */

const SOURCE_BADGE: Record<string, "info" | "success" | "neutral"> = {
  info: "info",
  success: "success",
  neutral: "neutral",
};

export function ConfigViewer() {
  const canExport = usePermission("evidence:export");
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-xl font-extrabold text-ink">Effective Config Viewer</h1>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={!canExport} onClick={() => toast({ variant: "info", title: "JSON downloaded (production only)" })}>⬇ Download JSON</Button>
          <Button variant="secondary" size="sm" disabled={!canExport} onClick={() => toast({ variant: "info", title: "YAML downloaded (production only)" })}>⬇ Download YAML</Button>
        </div>
      </div>

      <div className="mb-4 text-sm text-muted">
        environment: <span className="font-semibold text-ink">{SVC_CONFIG_META.environment}</span> · service: <span className="font-semibold text-ink">{SVC_CONFIG_META.service}</span> · as-of: {SVC_CONFIG_META.asOf} · {SVC_CONFIG_META.keyCount} effective keys
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="info" size="sm" dot={false}>Inherited</Badge>
        <Badge variant="success" size="sm" dot={false}>Override Applied</Badge>
        <Badge variant="neutral" size="sm" dot={false}>Default</Badge>
        <span className="ml-auto text-xs text-muted">Read-only view · changes go through config pipeline</span>
      </div>

      <ConsoleCard title="effective-config.json" meta={SVC_CONFIG_META.service}>
        <div className="space-y-1.5 p-4 font-mono text-xs leading-relaxed">
          {SVC_CONFIG_TREE.map((node, i) => (
            <div key={i} className="flex flex-wrap items-baseline gap-2" style={{ paddingLeft: (node.level - 1) * 20 }}>
              <span className="font-semibold text-console-accent">&quot;{node.key}&quot;</span>
              <span className="text-console-muted break-all">{node.value}</span>
              <Badge variant={SOURCE_BADGE[node.sourceTone]} size="sm" dot={false} className="whitespace-nowrap">{node.source}</Badge>
            </div>
          ))}
        </div>
      </ConsoleCard>
    </div>
  );
}
